import clsx from 'clsx';
import React from 'react';
import { useDispatch } from 'react-redux';
import { toggleSwitch } from '../../reducers/switch';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import ManagementReviewIcon from '../../assets/icons/management-review.svg?react';
import CorrectiveActionAssignedIcon from '../../assets/icons/corrective-action-assigned.svg?react';
import NotificationBellIcon from '../../assets/icons/notification-bell-icon.svg?react';
import ArrowRightIcon from '../../assets/icons/arrow-right.svg?react';
import Button from '../../components/Button';
import { useButtonSizeLarge } from '../../utils/buttonSizes';
import ReportIcon from '../../assets/icons/report-icon.svg?react';
import { Link } from 'react-router';
import moment from 'moment';

interface IncidentReportingStepFourProps {
  otherClasses?: string;
  submittedIncidentResponse: any;
  handleSteps: (steps: number) => void;
}

const IncidentReportingStepFour: React.FC<IncidentReportingStepFourProps> = ({
  otherClasses,
  submittedIncidentResponse,
  handleSteps,
}) => {
  const incidentReportingStepFourClasses = clsx(otherClasses);
  const dispatch = useDispatch();

  const whatHappenNext = [
    {
      icon: (
        <ManagementReviewIcon className="h-9 w-9 md:h-12 md:w-12 lg:h-75 lg:w-75" />
      ),
      title: 'Management Review',
    },
    {
      icon: (
        <CorrectiveActionAssignedIcon className="h-9 w-9 md:h-12 md:w-12 lg:h-75 lg:w-75" />
      ),
      title: 'Corrective Action Assigned',
    },
    {
      icon: (
        <NotificationBellIcon className="[&>g>path]:fill-brand-fair-orange h-9 w-9 md:h-12 md:w-12 lg:h-75 lg:w-75" />
      ),
      title: 'You’ll Receive Updates',
    },
  ];

  return (
    <div
      className={incidentReportingStepFourClasses}
      data-testid="incident-reporting-step-four"
    >
      <div className="flex max-w-818 items-start gap-2.5">
        <button
          className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
          onClick={() => dispatch(toggleSwitch())}
        >
          <ToggleIcon />
        </button>
        <div>
          <h1 className="h3">Your Report Has Been Submitted Successfully</h1>
          <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-xl leading-6 font-medium lg:block">
            Your report has been submitted for review to higher management. An
            action item will be assigned promptly to ensure timely resolution of
            the incident/hazard.
          </p>
        </div>
      </div>
      <div className="lg:border-y-neutral-tones-text/10 border-b-neutral-tones-text/10 mt-8 border-b pb-8 lg:mt-6 lg:border-y lg:py-9">
        <p className="font-inter-variable text-neutral-tones-text/80 -tracking-form-input mb-8 text-base leading-5 font-medium italic lg:hidden">
          Your report has been submitted for review to higher management. An
          action item will be assigned promptly to ensure timely resolution of
          the incident/hazard.
        </p>
        <div className="flex w-full flex-col items-start gap-8 md:flex-row md:gap-100">
          {submittedIncidentResponse?.incidentRefId && (
            <div className="flex flex-col gap-5">
              <h6 className="text-brand-byzantine font-inter-variable -tracking-text-area lg:-tracking-button text-lg leading-5 font-bold lg:text-xl lg:leading-6">
                Reference ID
              </h6>
              <p className="font-inter-variable text-neutral-tones-text text-base font-normal lg:text-xl">
                {submittedIncidentResponse.incidentRefId}
              </p>
            </div>
          )}
          <div className="flex flex-col gap-5">
            <h6 className="text-brand-byzantine font-inter-variable -tracking-text-area lg:-tracking-button text-lg leading-5 font-bold lg:text-xl lg:leading-6">
              Submitted On
            </h6>
            <p className="font-inter-variable text-neutral-tones-text text-base font-normal lg:text-xl">
              {moment(submittedIncidentResponse?.createdAt).format(
                'DD MMM YYYY, hh:mm A',
              )}
            </p>
          </div>
        </div>
        {/* <Button
          variant="secondary"
          size={useButtonSizeMdSmaller()}
          otherClasses="mt-6"
        >
          <DownloadIcon />
          Download Report
        </Button> */}
      </div>
      <div className="lg:border-b-neutral-tones-text/10 py-8 lg:border-b lg:py-9">
        <h4 className="text-brand-fair-orange mb-6">What Happen’s Next?</h4>
        <div className="flex items-center gap-2.5 md:gap-0">
          {whatHappenNext?.map(({ icon, title }, index) => (
            <div className="flex items-center gap-2.5 md:gap-0" key={index}>
              <div
                key={index}
                className="flex max-w-185 flex-col items-center gap-2.5 text-center"
              >
                {icon}
                <p className="font-inter-variable text-neutral-tones-text lg:leeading-6 md:-tracking-text-area lg:-tracking-button -tracking-form-input text-sm leading-4 font-medium md:text-lg md:leading-5 lg:text-xl">
                  {title}
                </p>
              </div>
              {whatHappenNext?.length - 1 !== index && (
                <ArrowRightIcon className="[&>g>path]:fill-neutral-tones-text h-6 min-h-6 w-6 min-w-6 md:h-11 md:min-h-11 md:w-11 md:min-w-11 lg:h-12 lg:min-h-12 lg:w-12 lg:min-w-12" />
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="py-9">
        <p className="font-inter-variable text-neutral-tones-text -tracking-text-area lg:-tracking-button mb-2.5 text-lg leading-5 font-normal lg:text-xl lg:leading-6">
          Would you like to submit another report?
        </p>
        <Button
          onClick={() => handleSteps(1)}
          otherClasses="italic"
          size={useButtonSizeLarge()}
        >
          <ReportIcon className="[&>path]:fill-current" />
          Submit New Report
        </Button>
        <p className="-tracking-button text-neutral-tones-text font-inter-variable mt-8 flex flex-col gap-2.5 text-xl leading-6 font-normal lg:mt-9 lg:flex-row lg:items-center lg:gap-5">
          Having trouble with something?{' '}
          <Link
            to="/contact-support"
            className="text-brand-fair-orange font-bold"
          >
            Contact Support
          </Link>
        </p>
      </div>
    </div>
  );
};

export default IncidentReportingStepFour;
